import React from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatDate } from 'date-fns';
import { groupBy, meanBy } from 'lodash';

import AssessmentChart from '../charts/AssessmentChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { KeyStudentEngagementAssessmentResult } from 'types/schema-generated-types';

const StudentAssessmentChart: React.FC<{
  reportData?: KeyStudentEngagementAssessmentResult[];
  dateIntervals: string[];
  dateIntervalFormat: string;
  loading: boolean;
}> = ({ reportData = [], dateIntervals, dateIntervalFormat, loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts']);

  const intervalValues = reportData.reduce(
    (acc, { dateCompleted, aborted, averageWpm, averageAccuracy }) => {
      if (!dateCompleted || aborted) return acc;
      return [
        ...acc,
        {
          date: formatDate(new Date(dateCompleted), dateIntervalFormat),
          averageWpm,
          averageAccuracy
        }
      ];
    },
    [] as { date: string; averageWpm: number; averageAccuracy: number }[]
  );

  const intervalValuesByDate = groupBy(intervalValues, 'date');
  const { wpmSeries, accuracySeries } = dateIntervals.reduce(
    (acc, date) => {
      const dateIntervalValues = intervalValuesByDate[date];
      if (!dateIntervalValues) {
        acc.wpmSeries.push(0);
        acc.accuracySeries.push(0);
      } else {
        acc.wpmSeries.push(meanBy(dateIntervalValues, 'averageWpm'));
        acc.accuracySeries.push(meanBy(dateIntervalValues, 'averageAccuracy'));
      }

      return acc;
    },
    {
      wpmSeries: [] as number[],
      accuracySeries: [] as number[]
    }
  );

  return (
    <ReportCard
      title={t('Teacher Assigned Assessments')}
      helpText={t('assessments', { ns: 'ReportsHelpCharts' })}
      loading={loading}
      reportData={reportData}
    >
      <AssessmentChart
        wpmSeries={wpmSeries}
        accuracySeries={accuracySeries}
        labels={dateIntervals}
      />
    </ReportCard>
  );
};

export default StudentAssessmentChart;
